import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_SERVICE } from "../Services/CommonApi";
import { commonPayloadFields } from "../helper/storage";
import {
  GET_PR_COMMENTS,
  SAVE_PR_COMMENT,
  DELETE_PR_COMMENT,
  SAVE_PR_QTY_REDUCTION,
  DECIDE_PR_QTY_REDUCTION,
} from "../constant/CommonApiUrl";
import {
  COMMENT_PAGE_SIZE,
  isFeatureDisabled,
  mergeComments,
} from "../helper/prDiscussion";

// PR Discussion tab. Contract: docs/pr-discussion-tab-backend-spec-updated.md.
//
// Every business outcome arrives as HTTP 200, which axios resolves — so each
// thunk turns a refusal into a rejection itself. Two kinds:
//   - status: false + messageCode (validation, FORBIDDEN, OUT_OF_SCOPE, …).
//   - FEATURE_DISABLED, which is status: TRUE with data: [] — the tenant isn't
//     in error, it just doesn't have the feature. The only quiet gate.
//
// Real HTTP errors carry no envelope: 401 (empty; CommonApi logs out), 400
// (ASP.NET ProblemDetails, a value that couldn't bind), and 404 — which on
// Purchase/* means an unhandled server exception, whose body is the raw exception
// text. A 404 is the backend misconfigured, not "not found" and not "not
// deployed", so it surfaces as an error rather than hiding the tab.

const rejectValue = (err, thunkAPI) => {
  const body = err?.response?.data;
  // The flat envelope, if the body is one. Never axios's own message: "Request
  // failed with status code 404" isn't something to show a user.
  const envelope =
    body && typeof body === "object" ? body.result || body : {};

  return thunkAPI.rejectWithValue({
    httpStatus: err?.response?.status,
    messageCode: envelope.messageCode,
    message: envelope.message,
    // Kept for debugging (e.g. "Invalid object name 'dbo.TblPRComment'."), not
    // for display.
    detail: typeof body === "string" ? body : err?.message,
  });
};

const refuse = (result, thunkAPI) =>
  thunkAPI.rejectWithValue({
    httpStatus: 200,
    messageCode: result?.messageCode,
    message: result?.message,
  });

const isRefusal = (result) =>
  result?.status === false || isFeatureDisabled(result);

// The feature is switched off for this tenant.
const isGated = (payload) => isFeatureDisabled(payload);

// `refresh: true` re-reads page 1 (the newest comments) without discarding pages
// the user already pulled in with "Load earlier" — used after a decision, which
// appends rows at the top.
export const fetchPRComments = createAsyncThunk(
  "prDiscussion/fetchComments",
  async (
    { prId, page = 1, pageSize = COMMENT_PAGE_SIZE, refresh = false },
    thunkAPI,
  ) => {
    try {
      const query = new URLSearchParams({
        pr_id: String(prId),
        page: String(page),
        page_size: String(pageSize),
      }).toString();
      const response = await API_SERVICE.get(`${GET_PR_COMMENTS}?${query}`);
      const result = response.data.result;

      if (isRefusal(result)) return refuse(result, thunkAPI);

      return { ...result, requestedPage: page, refresh };
    } catch (err) {
      return rejectValue(err, thunkAPI);
    }
  },
);

export const postPRComment = createAsyncThunk(
  "prDiscussion/postComment",
  async ({ prId, message }, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(SAVE_PR_COMMENT, {
        id: 0,
        pr_id: prId,
        type: "COMMENT",
        message,
        createdby: commonPayloadFields.createdby,
        createddt: new Date().toISOString(),
      });
      const result = response.data.result;

      if (isRefusal(result)) return refuse(result, thunkAPI);

      return result;
    } catch (err) {
      return rejectValue(err, thunkAPI);
    }
  },
);

export const editPRComment = createAsyncThunk(
  "prDiscussion/editComment",
  async ({ prId, commentId, message }, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(SAVE_PR_COMMENT, {
        id: commentId,
        pr_id: prId,
        message,
        updatedby: commonPayloadFields.updatedby,
        updateddt: new Date().toISOString(),
      });
      const result = response.data.result;

      if (isRefusal(result)) return refuse(result, thunkAPI);

      return result;
    } catch (err) {
      return rejectValue(err, thunkAPI);
    }
  },
);

// A plain comment disappears on delete; a QTY_REDUCTION stays as a tombstone with
// its reduction cancelled. The caller passes wasQtyReduction so the reducer knows
// which of the two happened without re-reading the row it just removed.
export const deletePRComment = createAsyncThunk(
  "prDiscussion/deleteComment",
  async ({ prId, commentId, wasQtyReduction = false }, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(DELETE_PR_COMMENT, {
        id: commentId,
        pr_id: prId,
        updatedby: commonPayloadFields.updatedby,
        updateddt: new Date().toISOString(),
      });
      const result = response.data.result;

      if (isRefusal(result)) return refuse(result, thunkAPI);

      return { ...result, commentId, wasQtyReduction };
    } catch (err) {
      return rejectValue(err, thunkAPI);
    }
  },
);

export const requestQtyReduction = createAsyncThunk(
  "prDiscussion/requestQtyReduction",
  async ({ prId, itemId, requestedQty, reason, message }, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(SAVE_PR_QTY_REDUCTION, {
        id: 0,
        pr_id: prId,
        item_id: itemId,
        requested_qty: requestedQty,
        reason,
        message: message || "",
        createdby: commonPayloadFields.createdby,
        createddt: new Date().toISOString(),
      });
      const result = response.data.result;

      if (isRefusal(result)) return refuse(result, thunkAPI);

      return result;
    } catch (err) {
      return rejectValue(err, thunkAPI);
    }
  },
);

export const decideQtyReduction = createAsyncThunk(
  "prDiscussion/decideQtyReduction",
  async ({ prId, reductionId, status, remark = "" }, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(DECIDE_PR_QTY_REDUCTION, {
        id: reductionId,
        pr_id: prId,
        status,
        remark,
        updatedby: commonPayloadFields.updatedby,
        updateddt: new Date().toISOString(),
      });
      const result = response.data.result;

      if (isRefusal(result)) return refuse(result, thunkAPI);

      return { ...result, reductionId, decidedStatus: status };
    } catch (err) {
      return rejectValue(err, thunkAPI);
    }
  },
  {
    // A second click on the same reduction while the first is in flight would be
    // refused by the server as ALREADY_DECIDED — so drop it here instead.
    condition: ({ reductionId }, { getState }) =>
      !(getState().root?.prDiscussion?.decidingIds || []).includes(reductionId),
  },
);

const initialState = {
  prId: null,
  rows: [], // newest first, matching the server's sort
  page: 1,
  totalPage: 0,
  totalData: 0,
  // Closed until a successful fetch says otherwise: refusal responses carry no
  // flags, and the backend's guidance is to read a missing flag as false.
  canComment: false,
  canRequestReduction: false,
  blockedReason: null,
  // Whether a fetch has succeeded for this PR. The composer waits for it, so a
  // refetch (Load earlier, or after a decision) doesn't make it flicker.
  answered: false,
  loadStatus: "idle", // idle | loading | loaded | error
  gate: "unknown", // unknown | enabled | disabled (feature off, or not deployed)
  posting: false,
  // Reductions currently being accepted/rejected. A PR can hold one pending
  // reduction per line, so several can be in flight at once — a single id would
  // let the second click clear the first row's in-progress state.
  decidingIds: [],
  error: null,
};

// Any failed call can reveal that the feature is off — including a write, if a
// tenant is switched off mid-session.
const applyGate = (state, payload) => {
  if (isGated(payload)) {
    state.gate = "disabled";
    return false;
  }
  return true;
};

// AddEdit endpoints return the affected row(s) in `data`. Tolerate both a bare
// object and the single-element array the Purchase controllers usually send.
const rowsFrom = (payload) => {
  const data = payload?.data;

  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return [data];

  return [];
};

// The decision endpoint returns a composite: the updated reduction comment, the
// SYSTEM comment it appended, and the new item figures. Any of them may be absent
// while the backend is still in flight, so pull out whatever is there.
const decisionComments = (payload) => {
  const [first] = rowsFrom(payload);

  if (!first) return [];
  if (first.comment || first.system_comment) {
    return [first.comment, first.system_comment].filter(Boolean);
  }

  return [first];
};

// A response for a PR the user has already navigated away from must not land in
// the new PR's thread. Compared as strings on purpose: the mount fetch passes the
// route param (a string) while writes pass req_id_details.id (a number).
const isStaleResponse = (state, meta) =>
  state.prId != null &&
  meta?.arg?.prId != null &&
  String(meta.arg.prId) !== String(state.prId);

const prDiscussionSlice = createSlice({
  name: "prDiscussion",
  initialState,
  reducers: {
    // Switching PRs must not leave the previous thread on screen. The gate is
    // kept: whether the route exists is a property of the deployment, not the PR.
    resetDiscussion: (state) => ({ ...initialState, gate: state.gate }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPRComments.pending, (state, { meta }) => {
        state.loadStatus = "loading";
        state.error = null;
        if (state.prId !== meta.arg.prId) {
          state.prId = meta.arg.prId;
          state.rows = [];
          state.totalData = 0;
          state.answered = false;
          state.canComment = false;
          state.canRequestReduction = false;
          state.blockedReason = null;
        }
      })
      .addCase(fetchPRComments.fulfilled, (state, { payload, meta }) => {
        if (isStaleResponse(state, meta)) return;

        state.loadStatus = "loaded";
        state.gate = "enabled";
        state.answered = true;

        const rows = Array.isArray(payload?.data) ? payload.data : [];
        // Only a fresh page-1 load replaces the thread. A refresh merges, so a
        // decision doesn't wipe the pages "Load earlier" already brought in, and
        // leaves `page` at the deepest page loaded so hasMore stays right.
        const keepLoaded = payload.requestedPage > 1 || payload.refresh;

        state.rows = keepLoaded
          ? mergeComments(state.rows, rows)
          : mergeComments([], rows);
        state.page = payload.refresh ? state.page : payload.requestedPage;
        state.totalPage = payload.totalPage || 0;
        state.totalData = payload.totalData ?? state.rows.length;
        // Only an explicit true opens a control. The server enforces every rule
        // regardless; this just avoids offering what it will refuse.
        state.canComment = payload.can_comment === true;
        state.canRequestReduction = payload.can_request_reduction === true;
        state.blockedReason = payload.blocked_reason ?? null;
      })
      .addCase(fetchPRComments.rejected, (state, { payload, meta }) => {
        if (isStaleResponse(state, meta)) return;

        // Flags are left alone: a failed refetch shouldn't close a composer the
        // server already opened. On a first-load failure they're still closed.
        if (!applyGate(state, payload)) {
          state.loadStatus = "loaded";
          state.rows = [];
          state.totalData = 0;
          return;
        }
        // OUT_OF_SCOPE, NOT_FOUND or a server fault. The feature exists; this
        // thread just can't be shown.
        state.gate = "enabled";
        state.loadStatus = "error";
        state.error = payload;
      })

      .addCase(postPRComment.pending, (state) => {
        state.posting = true;
      })
      .addCase(postPRComment.fulfilled, (state, { payload, meta }) => {
        state.posting = false;
        if (isStaleResponse(state, meta)) return;

        const rows = rowsFrom(payload);
        if (!rows.length) return;

        state.rows = mergeComments(state.rows, rows);
        state.totalData += rows.length;
      })
      .addCase(postPRComment.rejected, (state, { payload, meta }) => {
        state.posting = false;
        if (isStaleResponse(state, meta)) return;

        state.error = payload;
      })

      .addCase(editPRComment.pending, (state) => {
        state.posting = true;
      })
      .addCase(editPRComment.fulfilled, (state, { payload, meta }) => {
        state.posting = false;
        if (isStaleResponse(state, meta)) return;

        // An edit replaces a row that is already counted, so totalData is unchanged.
        state.rows = mergeComments(state.rows, rowsFrom(payload));
      })
      .addCase(editPRComment.rejected, (state, { payload, meta }) => {
        state.posting = false;
        if (isStaleResponse(state, meta)) return;

        state.error = payload;
      })

      .addCase(deletePRComment.fulfilled, (state, { payload, meta }) => {
        if (isStaleResponse(state, meta)) return;

        const returned = rowsFrom(payload);

        // A qty-reduction delete leaves a tombstone row behind. Prefer the row the
        // server sends; fall back to a local edit so the UI is right either way.
        if (returned.length) {
          state.rows = mergeComments(state.rows, returned);
        } else if (payload.wasQtyReduction) {
          state.rows = state.rows.map((row) =>
            row.id === payload.commentId
              ? {
                  ...row,
                  message: "This quantity reduction request was withdrawn.",
                  can_edit: false,
                  can_delete: false,
                  qty_reduction: row.qty_reduction
                    ? { ...row.qty_reduction, status: "CANCELLED", can_decide: false }
                    : null,
                }
              : row,
          );
        } else {
          state.rows = state.rows.filter((row) => row.id !== payload.commentId);
        }

        state.totalData =
          payload.totalData ??
          (payload.wasQtyReduction
            ? state.totalData
            : Math.max(0, state.totalData - 1));
      })
      .addCase(deletePRComment.rejected, (state, { payload, meta }) => {
        if (isStaleResponse(state, meta)) return;

        state.error = payload;
      })

      .addCase(requestQtyReduction.pending, (state) => {
        state.posting = true;
      })
      .addCase(requestQtyReduction.fulfilled, (state, { payload, meta }) => {
        state.posting = false;
        if (isStaleResponse(state, meta)) return;

        const rows = rowsFrom(payload);
        if (!rows.length) return;

        state.rows = mergeComments(state.rows, rows);
        state.totalData += rows.length;
      })
      .addCase(requestQtyReduction.rejected, (state, { payload, meta }) => {
        state.posting = false;
        if (isStaleResponse(state, meta)) return;

        state.error = payload;
      })

      .addCase(decideQtyReduction.pending, (state, { meta }) => {
        if (!state.decidingIds.includes(meta.arg.reductionId)) {
          state.decidingIds.push(meta.arg.reductionId);
        }
      })
      .addCase(decideQtyReduction.fulfilled, (state, { payload, meta }) => {
        state.decidingIds = state.decidingIds.filter(
          (id) => id !== meta.arg.reductionId,
        );
        if (isStaleResponse(state, meta)) return;

        const rows = decisionComments(payload);

        if (rows.length) {
          const before = state.rows.length;
          state.rows = mergeComments(state.rows, rows);
          state.totalData += state.rows.length - before;
          return;
        }

        // No rows came back: stamp the decision locally so the buttons clear.
        // PurchaseReqEdit refetches the thread and the PR either way.
        state.rows = state.rows.map((row) =>
          row.qty_reduction?.id === payload.reductionId
            ? {
                ...row,
                qty_reduction: {
                  ...row.qty_reduction,
                  status: payload.decidedStatus,
                  can_decide: false,
                },
              }
            : row,
        );
      })
      .addCase(decideQtyReduction.rejected, (state, { payload, meta }) => {
        state.decidingIds = state.decidingIds.filter(
          (id) => id !== meta.arg.reductionId,
        );
        if (isStaleResponse(state, meta)) return;

        state.error = payload;
      })
      // Runs after the case reducers above, so it only adds the gate check.
      .addMatcher(
        (action) =>
          action.type.startsWith("prDiscussion/") &&
          action.type.endsWith("/rejected"),
        (state, { payload }) => {
          applyGate(state, payload);
        },
      );
  },
});

export const { resetDiscussion } = prDiscussionSlice.actions;

export const selectDiscussion = (state) => state.root.prDiscussion;

export default prDiscussionSlice.reducer;
